import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useBitcoinData } from '../contexts/BitcoinDataContext';
import { 
  Activity, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Database, 
  Globe, 
  TrendingUp,
  Wifi,
  WifiOff,
  Zap 
} from 'lucide-react';

const Index = () => {
  const { state, actions } = useBitcoinData();
  const [currentTime, setCurrentTime] = useState(Date.now());

  // Update current time every second
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const formatTime = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const formatTimeDiff = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) return `${hours}h ${minutes}m ${secs}s`;
    if (minutes > 0) return `${minutes}m ${secs}s`;
    return `${secs}s`;
  };

  const getApiStatusIcon = (available: boolean) => {
    return available ? (
      <Wifi className="h-4 w-4 text-green-500" />
    ) : (
      <WifiOff className="h-4 w-4 text-red-500" />
    );
  };

  const getSeverityColor = (severity: 'minor' | 'major' | 'critical') => {
    switch (severity) {
      case 'minor': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'major': return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'critical': return 'bg-red-100 text-red-800 border-red-300';
      default: return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-4">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Bitcoin Timestamp Verifier
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Real-time cross-verification of Bitcoin block timestamps across multiple sources with anomaly detection and next-block prediction
          </p>
        </div>

        {/* Connection Status */}
        <div className="flex justify-center">
          <Badge variant={state.isConnected ? "default" : "destructive"} className="px-4 py-2">
            {state.isConnected ? (
              <>
                <Wifi className="h-4 w-4 mr-2" />
                Connected to Bitcoin Network
              </>
            ) : (
              <>
                <WifiOff className="h-4 w-4 mr-2" />
                Connecting...
              </>
            )}
          </Badge>
        </div>

        {/* Error Alert */}
        {state.error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{state.error}</AlertDescription>
          </Alert>
        )}

        {/* Main Dashboard */}
        <Tabs defaultValue="live" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4 max-w-md mx-auto">
            <TabsTrigger value="live">Live Monitor</TabsTrigger>
            <TabsTrigger value="verification">Verification</TabsTrigger>
            <TabsTrigger value="predictions">Predictions</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>

          {/* Live Monitoring Tab */}
          <TabsContent value="live" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Latest Block */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center">
                    <Database className="h-4 w-4 mr-2" />
                    Latest Block
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {state.latestBlock ? (
                    <div className="space-y-2">
                      <div className="text-2xl font-bold">#{state.latestBlock.height}</div>
                      <div className="text-xs text-slate-600">
                        {formatTime(state.latestBlock.timestamp)}
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {state.latestBlock.source}
                      </Badge>
                    </div>
                  ) : (
                    <div className="text-slate-400">No block data</div>
                  )}
                </CardContent>
              </Card>

              {/* API Health */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center">
                    <Activity className="h-4 w-4 mr-2" />
                    API Health
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(state.apiHealth).map(([api, status]) => (
                      <div key={api} className="flex items-center justify-between">
                        <span className="text-xs capitalize">{api}</span>
                        {getApiStatusIcon(status.available)}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Anomalies */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center">
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    Recent Anomalies
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600">
                    {state.recentAnomalies.length}
                  </div>
                  <div className="text-xs text-slate-600">
                    {state.recentAnomalies.length > 0 && (
                      <>Last: Block #{state.recentAnomalies[0].blockHeight}</>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Next Block Prediction */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center">
                    <Clock className="h-4 w-4 mr-2" />
                    Next Block ETA
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {state.predictions ? (
                    <div className="space-y-2">
                      <div className="text-2xl font-bold">
                        {formatTimeDiff(state.predictions.nextBlockTime.estimate)}
                      </div>
                      <div className="text-xs text-slate-600">
                        {Math.round(state.predictions.nextBlockTime.confidence * 100)}% confidence
                      </div>
                    </div>
                  ) : (
                    <div className="text-slate-400">Calculating...</div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Manual Verification */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Manual Block Verification
                </CardTitle>
                <CardDescription>
                  Verify a specific block height across all data sources
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex gap-4 items-end">
                  <div className="flex-1">
                    <label className="text-sm font-medium">Block Height</label>
                    <input
                      type="number"
                      className="w-full mt-1 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter block height"
                      id="blockHeight"
                      min="0"
                    />
                  </div>
                  <Button
                    onClick={() => {
                      const input = document.getElementById('blockHeight') as HTMLInputElement;
                      const height = parseInt(input.value);
                      if (height && height > 0) {
                        actions.verifyBlock(height);
                      }
                    }}
                    disabled={state.isLoading}
                  >
                    {state.isLoading ? (
                      <>
                        <Zap className="h-4 w-4 mr-2 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Verify Block
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Verification Results Tab */}
          <TabsContent value="verification" className="space-y-6">
            {state.currentVerification ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Block #{state.currentVerification.blockHeight} Verification</span>
                    <Badge variant={state.currentVerification.consensus ? "default" : "destructive"}>
                      {state.currentVerification.consensus ? "Consensus" : "Anomaly Detected"}
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    Cross-verification results from {Object.keys(state.currentVerification.sources).length} data sources
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Source Timestamps */}
                  <div>
                    <h4 className="font-medium mb-3">Source Timestamps</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {Object.entries(state.currentVerification.sources).map(([source, timestamp]) => {
                        const drift = state.currentVerification!.drift[source];
                        const isAnomaly = drift > 7200; // 2 hours
                        
                        return (
                          <div key={source} className={`p-3 rounded-lg border ${isAnomaly ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium capitalize">{source}</span>
                              {isAnomaly && <AlertTriangle className="h-4 w-4 text-red-500" />}
                            </div>
                            <div className="text-sm text-slate-600">
                              {formatTime(timestamp)}
                            </div>
                            <div className="text-xs text-slate-500">
                              Drift: {formatTimeDiff(drift)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Median Timestamp */}
                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                    <div className="font-medium text-blue-900 mb-1">Median Timestamp</div>
                    <div className="text-blue-700">{formatTime(state.currentVerification.median)}</div>
                  </div>

                  {/* Anomalies */}
                  {state.currentVerification.anomalies.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-3 text-red-700">Detected Anomalies</h4>
                      <div className="space-y-2">
                        {state.currentVerification.anomalies.map((anomaly, index) => (
                          <div key={index} className={`p-3 rounded-lg border ${getSeverityColor(anomaly.severity)}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="font-medium capitalize">{anomaly.source}</span>
                              <Badge variant="outline" className="capitalize">
                                {anomaly.severity}
                              </Badge>
                            </div>
                            <div className="text-sm">{anomaly.message}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="flex items-center justify-center py-12">
                  <div className="text-center text-slate-500">
                    <Globe className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No verification results yet</p>
                    <p className="text-sm">Verify a block to see cross-source analysis</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Predictions Tab */}
          <TabsContent value="predictions" className="space-y-6">
            {state.predictions ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <TrendingUp className="h-5 w-5 mr-2" />
                      Next Block Prediction
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <div className="text-3xl font-bold text-blue-600 mb-2">
                        {formatTimeDiff(state.predictions.nextBlockTime.estimate)}
                      </div>
                      <div className="text-sm text-slate-600">
                        Estimated time until next block
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm">Confidence</span>
                        <span className="text-sm font-medium">
                          {Math.round(state.predictions.nextBlockTime.confidence * 100)}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm">Range</span>
                        <span className="text-sm font-medium">
                          {formatTimeDiff(state.predictions.nextBlockTime.range[0])} - {formatTimeDiff(state.predictions.nextBlockTime.range[1])}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Block Time Statistics</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm">Mean Block Time</span>
                      <span className="text-sm font-medium">
                        {formatTimeDiff(state.predictions.blockTimeStats.mean)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Median Block Time</span>
                      <span className="text-sm font-medium">
                        {formatTimeDiff(state.predictions.blockTimeStats.median)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Standard Deviation</span>
                      <span className="text-sm font-medium">
                        {formatTimeDiff(state.predictions.blockTimeStats.std)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm">Clean Data Points</span>
                      <span className="text-sm font-medium">
                        {state.predictions.blockTimeStats.cleanDataPoints}
                      </span>
                    </div>
                    <div className="flex justify-between text-green-600">
                      <span className="text-sm">Accuracy Improvement</span>
                      <span className="text-sm font-medium">
                        +{state.predictions.forecastAccuracy.improvementFromCleaning.toFixed(1)}%
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="flex items-center justify-center py-12">
                  <div className="text-center text-slate-500">
                    <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Prediction engine initializing</p>
                    <p className="text-sm">Collecting block data for accurate forecasting</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Anomaly History</CardTitle>
                <CardDescription>
                  Historical anomalies detected across all verified blocks
                </CardDescription>
              </CardHeader>
              <CardContent>
                {state.recentAnomalies.length > 0 ? (
                  <div className="space-y-3">
                    {state.recentAnomalies.slice(0, 10).map((anomaly) => (
                      <div key={anomaly.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border">
                        <div>
                          <div className="font-medium">Block #{anomaly.blockHeight}</div>
                          <div className="text-sm text-slate-600">
                            {anomaly.verification.anomalies.length} anomalies detected
                          </div>
                          <div className="text-xs text-slate-500">
                            {new Date(anomaly.detectedAt).toLocaleString()}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant={anomaly.resolved ? "default" : "destructive"}>
                            {anomaly.resolved ? "Resolved" : "Active"}
                          </Badge>
                          <Badge variant="outline">
                            {anomaly.verification.anomalies[0]?.severity || 'unknown'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center text-slate-500 py-8">
                    <Database className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>No anomalies recorded yet</p>
                    <p className="text-sm">Start verifying blocks to build anomaly history</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Footer */}
        <div className="text-center text-xs text-slate-500 py-4">
          Last updated: {new Date(currentTime).toLocaleTimeString()} • 
          Bitcoin Timestamp Verification System • 
          Built with React + TypeScript
        </div>
      </div>
    </div>
  );
};

export default Index;